import { useState } from 'react';

/**
 * Returns a `[value, setValue]` pair as with `useState`, but deferring to a controlled value if present.
 * This can be used to create components that function either in a controlled or uncontrolled mode.
 *
 * In uncontrolled mode (when `controlledValue` is undefined), `value` and `setValue` behave exactly as
 * `setState` given `defaultValue` during initialization.
 * Calling `setValue` will immediately update `value` to match the new value provided, as well as calling
 * the `onUpdate` callback.
 *
 * In controlled mode (when `controlledValue` is defined), `value` is always the same as `controlledValue`.
 * Calling `setValue` will call the `onUpdate` callback with the new value provided.
 */
function useControllableState(controlledValue, defaultValue, onUpdate) {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const value = controlledValue ?? uncontrolledValue;
  const callback = value => {
    setUncontrolledValue(value);
    if (onUpdate) {
      onUpdate(value);
    }
  };
  return [value, callback];
}

export { useControllableState };
